import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Pencil, Eye, EyeOff, RefreshCw, Copy } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ROLE_BADGE = {
  owner: "bg-violet-100 text-violet-700",
  manager: "bg-blue-100 text-blue-700",
  office: "bg-emerald-100 text-emerald-700",
  field: "bg-amber-100 text-amber-700",
};

const ROLE_LABEL = {
  owner: "Admin",
  manager: "Manager",
  office: "Office",
  field: "Field",
};

// Generate a strong, easy-to-share password (no ambiguous chars like O/0, l/1).
function genPassword(len = 12) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789@#$%";
  const arr = new Uint32Array(len);
  crypto.getRandomValues(arr);
  let p = "";
  for (let i = 0; i < len; i++) p += chars[arr[i] % chars.length];
  return p;
}

function CredRow({ label, value, onCopy }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-400 w-20">{label}</span>
      <code className="flex-1 text-sm bg-slate-50 border border-slate-200 rounded px-2 py-1.5 font-mono truncate">{value}</code>
      <button onClick={() => onCopy(value)} className="text-slate-400 hover:text-slate-700" title={`Copy ${label}`}>
        <Copy className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function Team() {
  const { user, setUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "office", phone: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [showEditPw, setShowEditPw] = useState(false);
  const [shownCreds, setShownCreds] = useState(null);

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => {
    load();
  }, []);

  const isOwner = user?.role === "owner";

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success("Copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const openEdit = (u) => {
    setEditForm({ id: u.id, name: u.name || "", email: u.email || "", phone: u.phone || "", role: u.role, password: "", can_delete: !!u.can_delete });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.name.trim() || !editForm.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    try {
      const payload = { name: editForm.name, email: editForm.email, phone: editForm.phone, role: editForm.role, can_delete: editForm.can_delete };
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/users/${editForm.id}`, payload);
      toast.success("Member updated");
      setEditOpen(false);
      load();
      if (editForm.password) setShownCreds({ email: editForm.email, password: editForm.password });
      if (editForm.id === user?.id) {
        const { data } = await api.get("/auth/me");
        setUser(data);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const create = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error("Name, email and password are required");
      return;
    }
    try {
      await api.post("/auth/register", form);
      toast.success("Team member added");
      setOpen(false);
      setShownCreds({ email: form.email, password: form.password });
      setForm({ name: "", email: "", password: "", role: "office", phone: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleDelete = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { can_delete: !u.can_delete });
      toast.success(!u.can_delete ? `${u.name} can now delete leads` : `Delete access removed for ${u.name}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="team-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">Team</h1>
          <p className="text-slate-500 text-sm">Manage office & field members and roles</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-member-btn" className="bg-blue-600 hover:bg-blue-700">
          <UserPlus className="h-4 w-4 mr-1.5" /> Add Member
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <th className="text-left font-medium py-3 px-5">Name</th>
              <th className="text-left font-medium py-3 px-4">Email</th>
              <th className="text-left font-medium py-3 px-4">Phone</th>
              <th className="text-left font-medium py-3 px-4">Role</th>
              <th className="text-left font-medium py-3 px-4">Delete access</th>
              <th className="text-right font-medium py-3 px-5"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100" data-testid={`team-member-${u.id}`}>
                <td className="py-3 px-5 font-medium text-slate-900">{u.name}</td>
                <td className="py-3 px-4 text-slate-600">{u.email}</td>
                <td className="py-3 px-4 text-slate-600">{u.phone || "—"}</td>
                <td className="py-3 px-4">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${ROLE_BADGE[u.role]}`}>{ROLE_LABEL[u.role] || u.role}</span>
                </td>
                <td className="py-3 px-4">
                  {u.role === "owner" || u.role === "manager" ? (
                    <span className="text-xs text-slate-400" title="Delete is always allowed for this role">Always (role)</span>
                  ) : (isOwner || (user?.role === "manager" && u.role !== "owner")) ? (
                    <button
                      onClick={() => toggleDelete(u)}
                      data-testid={`toggle-delete-${u.id}`}
                      role="switch"
                      aria-checked={!!u.can_delete}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${u.can_delete ? "bg-blue-600" : "bg-slate-300"}`}
                      title={u.can_delete ? "Can delete leads — click to revoke" : "Cannot delete leads — click to allow"}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${u.can_delete ? "translate-x-4" : "translate-x-0.5"}`} />
                    </button>
                  ) : (
                    <span className="text-xs text-slate-400">{u.can_delete ? "Allowed" : "—"}</span>
                  )}
                </td>
                <td className="py-3 px-5 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {(isOwner || (user?.role === "manager" && u.role !== "owner")) && (
                      <button onClick={() => openEdit(u)} data-testid={`edit-${u.id}`} className="text-slate-400 hover:text-slate-700">
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                    {user?.role === "owner" && u.role !== "owner" && (
                      <button onClick={() => remove(u.id)} data-testid={`remove-${u.id}`} className="text-red-500 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-member-dialog">
          <DialogHeader><DialogTitle className="font-display">Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Full Name</Label>
              <Input data-testid="member-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Email</Label>
                <Input data-testid="member-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Phone (for WhatsApp)</Label>
                <Input data-testid="member-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91..." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Password</Label>
                  <button type="button" onClick={() => setForm({ ...form, password: genPassword() })} data-testid="gen-member-password" className="text-[11px] text-blue-600 hover:underline flex items-center gap-1">
                    <RefreshCw className="h-3 w-3" /> Generate
                  </button>
                </div>
                <div className="relative">
                  <Input data-testid="member-password" type={showPw ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="pr-9" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="member-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="office">Office</SelectItem>
                    <SelectItem value="field">Field</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={create} data-testid="save-member-btn" className="bg-blue-600 hover:bg-blue-700">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-member-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit Member</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Full Name</Label>
                <Input data-testid="edit-member-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Email (login)</Label>
                  <Input data-testid="edit-member-email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Phone (for WhatsApp)</Label>
                  <Input data-testid="edit-member-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="+91..." />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">New Password (optional)</Label>
                    <button type="button" onClick={() => setEditForm({ ...editForm, password: genPassword() })} data-testid="gen-edit-password" className="text-[11px] text-blue-600 hover:underline flex items-center gap-1">
                      <RefreshCw className="h-3 w-3" /> Generate
                    </button>
                  </div>
                  <div className="relative">
                    <Input data-testid="edit-member-password" type={showEditPw ? "text" : "password"} value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Leave blank to keep" className="pr-9" />
                    <button type="button" onClick={() => setShowEditPw(!showEditPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                      {showEditPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Role</Label>
                  <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                    <SelectTrigger data-testid="edit-member-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {isOwner && <SelectItem value="owner">Admin</SelectItem>}
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="office">Office</SelectItem>
                      <SelectItem value="field">Field</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {(editForm.role === "office" || editForm.role === "field") && (
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                  <input
                    type="checkbox"
                    data-testid="edit-member-can-delete"
                    checked={!!editForm.can_delete}
                    onChange={(e) => setEditForm({ ...editForm, can_delete: e.target.checked })}
                    className="h-4 w-4 accent-blue-600"
                  />
                  Allow this member to delete leads/cards
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="save-edit-member-btn" className="bg-blue-600 hover:bg-blue-700">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!shownCreds} onOpenChange={(v) => !v && setShownCreds(null)}>
        <DialogContent data-testid="creds-dialog">
          <DialogHeader><DialogTitle className="font-display">Login details</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">Copy and share these now — for security, the password won't be shown again.</p>
          <div className="space-y-2 py-2">
            <CredRow label="Email" value={shownCreds?.email} onCopy={copy} />
            <CredRow label="Password" value={shownCreds?.password} onCopy={copy} />
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="copy-both-creds" onClick={() => copy(`Email: ${shownCreds?.email}\nPassword: ${shownCreds?.password}`)}>
              <Copy className="h-4 w-4 mr-1.5" /> Copy both
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShownCreds(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
